/**
 * SettingsDisplaySection
 *
 * Display preferences: time grid density, snap interval, month view max events,
 * event times in month view, week view anchor, agenda grouping, event color priority.
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Monitor } from 'lucide-react';
import type { CalendarSettings } from '../types';
import { Section, ToggleRow, Field } from './settings-helpers';

interface SettingsDisplaySectionProps {
  settings: CalendarSettings;
  onUpdateSettings: (update: Partial<CalendarSettings>) => void;
}

export function SettingsDisplaySection({
  settings,
  onUpdateSettings,
}: SettingsDisplaySectionProps) {
  return (
    <Section icon={Monitor} title="Display">
      <Field label="Time grid density">
        <Select
          value={settings.timeGridDensity ?? 'default'}
          onValueChange={(v) =>
            onUpdateSettings({ timeGridDensity: v as 'compact' | 'default' | 'comfortable' })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="compact">Compact (40px/hr)</SelectItem>
            <SelectItem value="default">Default (60px/hr)</SelectItem>
            <SelectItem value="comfortable">Comfortable (80px/hr)</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Snap interval">
        <Select
          value={String(settings.snapIntervalMinutes ?? 15)}
          onValueChange={(v) => onUpdateSettings({ snapIntervalMinutes: parseInt(v, 10) })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="5">5 minutes</SelectItem>
            <SelectItem value="10">10 minutes</SelectItem>
            <SelectItem value="15">15 minutes</SelectItem>
            <SelectItem value="30">30 minutes</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Max events in month view">
        <Select
          value={String(settings.monthViewMaxEvents ?? 3)}
          onValueChange={(v) => onUpdateSettings({ monthViewMaxEvents: parseInt(v, 10) })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Show all</SelectItem>
            <SelectItem value="2">2</SelectItem>
            <SelectItem value="3">3</SelectItem>
            <SelectItem value="4">4</SelectItem>
            <SelectItem value="5">5</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <ToggleRow
        label="Show event times in month view"
        checked={settings.showEventTimesInMonthView ?? false}
        onCheckedChange={(checked) => onUpdateSettings({ showEventTimesInMonthView: checked })}
      />

      <Field label="Week view anchor">
        <Select
          value={settings.weekViewAnchor ?? 'fixed'}
          onValueChange={(v) => onUpdateSettings({ weekViewAnchor: v as 'fixed' | 'rolling' })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fixed">Fixed (starts on week-start day)</SelectItem>
            <SelectItem value="rolling">Rolling (starts on today)</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Agenda grouping">
        <Select
          value={settings.agendaGrouping ?? 'day'}
          onValueChange={(v) =>
            onUpdateSettings({ agendaGrouping: v as 'day' | 'calendar' | 'flat' })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">By day</SelectItem>
            <SelectItem value="calendar">By calendar</SelectItem>
            <SelectItem value="flat">Flat list</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Event color priority">
        <Select
          value={settings.eventColorPriority ?? 'event'}
          onValueChange={(v) => onUpdateSettings({ eventColorPriority: v as 'event' | 'calendar' })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="event">Event color first</SelectItem>
            <SelectItem value="calendar">Calendar color first</SelectItem>
          </SelectContent>
        </Select>
      </Field>
    </Section>
  );
}
